import { useEffect } from "react"
import { cn } from "@/lib/utils"

export interface Toast {
  id: string
  title: string
  description?: string
  variant?: "default" | "success" | "destructive" | "confirmation"
  actions?: {
    confirm?: {
      label: string
      onClick: () => void
    }
    cancel?: {
      label: string
      onClick: () => void
    }
  }
}

interface ToastProps {
  toast: Toast
  onDismiss: (id: string) => void
}

export function ToastComponent({ toast, onDismiss }: ToastProps) {
  useEffect(() => {
    // Don't auto-dismiss confirmation toasts with actions
    if (toast.actions) return

    const timer = setTimeout(() => {
      onDismiss(toast.id)
    }, 5000) // Auto dismiss after 5 seconds

    return () => clearTimeout(timer)
  }, [toast.id, onDismiss, toast.actions])

  const variantStyles = {
    default: "bg-white border-gray-200",
    success: "bg-green-50 border-green-200",
    destructive: "bg-red-50 border-red-200",
    confirmation: "bg-blue-50 border-blue-200"
  }

  const iconColors = {
    default: "text-gray-500",
    success: "text-green-500",
    destructive: "text-red-500",
    confirmation: "text-blue-500"
  }

  return (
    <div
      className={cn(
        "pointer-events-auto w-full max-w-md overflow-hidden rounded-lg border shadow-lg transition-all",
        variantStyles[toast.variant || "default"],
        "animate-in slide-in-from-right duration-300"
      )}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {toast.variant === "success" && (
              <svg className={cn("h-5 w-5", iconColors.success)} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {toast.variant === "destructive" && (
              <svg className={cn("h-5 w-5", iconColors.destructive)} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            {toast.variant === "confirmation" && (
              <svg className={cn("h-5 w-5", iconColors.confirmation)} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a1 1 0 011 1v3a1 1 0 01-2 0V6a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
            )}
            {(!toast.variant || toast.variant === "default") && (
              <svg className={cn("h-5 w-5", iconColors.default)} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 leading-tight">{toast.title}</p>
            {toast.description && (
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{toast.description}</p>
            )}
            {toast.actions && (
              <div className="mt-3 flex gap-2">
                {toast.actions.cancel && (
                  <button
                    onClick={() => {
                      toast.actions?.cancel?.onClick()
                      onDismiss(toast.id)
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                  >
                    {toast.actions.cancel.label}
                  </button>
                )}
                {toast.actions.confirm && (
                  <button
                    onClick={() => {
                      toast.actions?.confirm?.onClick()
                      onDismiss(toast.id)
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  >
                    {toast.actions.confirm.label}
                  </button>
                )}
              </div>
            )}
          </div>
          {!toast.actions && (
            <div className="flex flex-shrink-0 ml-4">
              <button
                onClick={() => onDismiss(toast.id)}
                className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      <div className="fixed top-4 right-4 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastComponent key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </div>
  )
}